import { uploadJobPDF, savePDFUrlToJob } from '../services/pdf-storage';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ═══════════════════════════════════════════════════════════════════════════════
// TWP FLEX SCREEN ORDER PDF — SINGLE JOB — LANDSCAPE
//
// Design: white bg, teal autoTable headers, brown brand stripe left, info fields row
//
// Column order matches FlexScreen Order manufacturer sheet exactly:
//   QTY | SCR WIDTH | SCR HEIGHT | MESH TYPE | SCREEN TYPE | ADAPTOR COLOR |
//   SCREEN SIZE | WIN LABEL | SCREEN LABEL | WIN TYPE
//
// ADAPTOR COLOR — derived from win.color (Excel FlexScreen Order col Z formula):
//   White / Dk Woodgrain/White → "WHT"
//   Clay  / Dk Woodgrain/Clay  → "TAN"
//   anything else              → "??"
//
// SCREEN LABEL = win.label + "-Scr"  (Excel Label Field 2)
// WIN LABEL    = win.label            (Excel Label Field 1)
// SCREEN SIZE and WIN TYPE kept as internal TWP reference fields.
// Dimensions: ALL decimals, no fractions.
// ═══════════════════════════════════════════════════════════════════════════════

// ── Palette ───────────────────────────────────────────────────────────────────
const BRAND_BROWN = [139,  69,  19];
const BRAND_LIGHT = [200, 160, 110];
const WHITE       = [255, 255, 255];
const LIGHT_GRAY  = [240, 240, 240];
const MID_GRAY    = [180, 180, 180];
const DARK        = [ 40,  40,  40];
const LABEL_COLOR = [100,  60,  20];
const TEAL_BG     = [220, 245, 240];
const TEAL_TEXT   = [  0,  90,  70];
const WHT_CLR     = [ 20,  80, 160];  // blue  — WHT adaptor
const TAN_CLR     = [140,  80,  10];  // amber — TAN adaptor
const HALF_CLR    = [180,  80,   0];  // orange — Half Scrn

const PAGE_W   = 279;
const PAGE_H   = 216;
const MARGIN   = 14;
const HEADER_H = 34;
const FOOTER_H = 12;

// ── Adaptor color — from Excel FlexScreen Order col Z formula ─────────────────
// IF(color="white","WHT", IF(color="Clay","TAN",
//    IF(color="Dk woodgrain/White","WHT", IF(color="Dk Woodgrain/Clay","TAN","??"))))
function adaptorColor(color) {
  if (!color) return '—';
  const c = color.toLowerCase().trim();
  if (c.includes('white')) return 'WHT';
  if (c.includes('clay'))  return 'TAN';
  return '??';
}

// ── Decimal display — strips trailing zeros, appends " ───────────────────────
function toDec(val) {
  if (val === null || val === undefined || val === '') return '—';
  const n = parseFloat(val);
  if (isNaN(n)) return String(val);
  return parseFloat(n.toFixed(4)).toString() + '"';
}

// ── Header ────────────────────────────────────────────────────────────────────
function drawHeader(doc, job, pageNum, totalPages) {
  doc.setFillColor(...WHITE);
  doc.rect(0, 0, PAGE_W, HEADER_H, 'F');

  // Left brand stripe
  doc.setDrawColor(...BRAND_LIGHT);
  doc.setLineWidth(3);
  doc.line(0, 0, 0, HEADER_H);

  // Bottom rule
  doc.setLineWidth(0.8);
  doc.line(0, HEADER_H, PAGE_W, HEADER_H);
  doc.setLineWidth(0.3);

  // Logo
  try {
    doc.addImage('/brand/twp-logo.png', 'PNG', MARGIN, 3, 18, 18);
  } catch (e) {}

  // Company name + address
  doc.setTextColor(...BRAND_BROWN);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('The Window Place LLC', MARGIN + 22, 11);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...LABEL_COLOR);
  doc.text('1300 E 4th Ave  •  Milbank, SD 57252  •  (605) 432-1834  •  thewindowplacellc.com', MARGIN + 22, 16.5);

  // Doc title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...TEAL_TEXT);
  doc.text('FLEXSCREEN ORDER', PAGE_W - MARGIN, 14, { align: 'right' });

  // Divider
  doc.setDrawColor(...LIGHT_GRAY);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, 21, PAGE_W - MARGIN, 21);

  // Info fields row
  const fields = [
    ['Customer', job.customerName || '—'],
    ['Job',      job.jobName      || '—'],
    ['WO #',     job.woNumber     || '—'],
    ['Date',     job.orderDate    || '—'],
    ['Page',     `${pageNum} of ${totalPages}`],
  ];
  let fx = MARGIN;
  fields.forEach(([label, value]) => {
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...LABEL_COLOR);
    doc.text(label + ':', fx, 26.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...DARK);
    doc.text(value, fx, 31);
    fx += 50;
  });
}

// ── Footer ────────────────────────────────────────────────────────────────────
function drawFooter(doc, totalQty) {
  const y = PAGE_H - FOOTER_H;
  doc.setFillColor(...WHITE);
  doc.rect(0, y, PAGE_W, FOOTER_H, 'F');

  doc.setDrawColor(...BRAND_LIGHT);
  doc.setLineWidth(3);
  doc.line(0, y, 0, PAGE_H);
  doc.setLineWidth(0.8);
  doc.line(0, y, PAGE_W, y);
  doc.setLineWidth(0.3);

  doc.setTextColor(...LABEL_COLOR);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(
    'The Window Place LLC  •  1300 E 4th Ave, Milbank, SD 57252  •  (605) 432-1834  •  office@thewindowplacellc.com',
    PAGE_W / 2, y + 4.5, { align: 'center' }
  );
  const now = new Date();
  const printed = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + '  ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  doc.setTextColor(...MID_GRAY);
  doc.setFontSize(6.5);
  doc.text(`Total Screens: ${totalQty}  •  Printed: ${printed}`, PAGE_W / 2, y + 9, { align: 'center' });
}

// ── returnBlob: true → returns Blob (for zip), false → saves file directly ───
export function generateFlexScreenOrderPDF(job, calculatedWindows, returnBlob = false) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });

  // ── Build rows ───────────────────────────────────────────────────────────────
  // Column order matches Excel FlexScreen Order manufacturer sheet:
  //   QTY | SCR WIDTH | SCR HEIGHT | MESH TYPE | SCREEN TYPE | ADAPTOR COLOR |
  //   SCREEN SIZE | WIN LABEL | SCREEN LABEL | WIN TYPE
  // NO MERGE — every window is its own screen row.
  // Standalone windows must NEVER be collapsed together, even when the screen
  // size/mesh/type/color match: they are physically separate screens with
  // separate labels (e.g. 8934-2 and 8934-7). Merging summed their quantities
  // and dropped the second label. Same rule as glass + shop order rollup.
  // Only the PW-first ordering is preserved.
  const norm = v => (v === null || v === undefined) ? '' : String(v).trim().toLowerCase();
  const isPW = w => norm(w.type || w.code || w.series).startsWith('pw') || norm(w.series).includes('picture');
  let rolledScreenWins = calculatedWindows
    .filter(w => w.flexScreen)
    .map((w, i) => ({ ...w, qty: parseFloat(w.qty) || 1, __idx: i }))
    .map((r, i) => ({ r, i, pw: isPW(r) ? 0 : 1 }))
    .sort((a, b) => a.pw - b.pw || a.i - b.i)
    .map(x => x.r);

  const rows = [];
  rolledScreenWins.forEach(win => {
    if (!win.flexScreen) return;
    const fs     = win.flexScreen;
    const color  = win.color || win.frameColor || fs.color || '';
    const aColor = adaptorColor(color);

    rows.push([
      String(win.qty ?? fs.qty ?? 1),           // QTY
      toDec(fs.width),                           // SCR WIDTH
      toDec(fs.height),                          // SCR HEIGHT
      fs.meshType   || win.meshType   || '—',    // MESH TYPE
      fs.screenType || win.screenType || '—',    // SCREEN TYPE
      aColor,                                    // ADAPTOR COLOR
      win.screenSize || fs.screenSize || '—',    // SCREEN SIZE
      win.label      || '—',                     // WIN LABEL  (Label Field 1)
      win.label ? `${win.label}-Scr` : 'Screen', // SCREEN LABEL (Label Field 2)
      win.type       || '—',                     // WIN TYPE
    ]);
  });

  if (rows.length === 0) {
    if (!returnBlob) alert('No FlexScreen windows in this job.');
    return returnBlob ? null : undefined;
  }

  const totalQty = rows.reduce((a, r) => a + (parseInt(r[0]) || 1), 0);

  const ROWS_PER_PAGE = 18;
  const pages = [];
  for (let i = 0; i < rows.length; i += ROWS_PER_PAGE) pages.push(rows.slice(i, i + ROWS_PER_PAGE));
  const totalPages = pages.length;

  const HEAD = [['QTY', 'SCR WIDTH', 'SCR HEIGHT', 'MESH TYPE', 'SCREEN TYPE', 'ADAPTOR\nCOLOR', 'SCREEN\nSIZE', 'WIN LABEL', 'SCREEN LABEL', 'WIN TYPE']];

  pages.forEach((pageRows, pageIdx) => {
    if (pageIdx > 0) doc.addPage();

    drawHeader(doc, job, pageIdx + 1, totalPages);
    drawFooter(doc, totalQty);

    autoTable(doc, {
      startY:  HEADER_H + 5,
      head:    HEAD,
      body:    pageRows,
      styles: {
        fontSize:    7.5,
        cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
        textColor:   DARK,
        lineColor:   LIGHT_GRAY,
        lineWidth:   0.2,
        overflow:    'hidden',
        halign:      'center',
      },
      headStyles: {
        fillColor:     TEAL_BG,
        textColor:     TEAL_TEXT,
        fontStyle:     'bold',
        fontSize:      7.5,
        halign:        'center',
        valign:        'middle',
        minCellHeight: 10,
        overflow:      'hidden',
      },
      alternateRowStyles: { fillColor: [245, 252, 250] },
      columnStyles: {
        0: { cellWidth: 10 },  // QTY
        1: { cellWidth: 22 },  // SCR WIDTH
        2: { cellWidth: 22 },  // SCR HEIGHT
        3: { cellWidth: 34 },  // MESH TYPE
        4: { cellWidth: 34 },  // SCREEN TYPE
        5: { cellWidth: 16 },  // ADAPTOR COLOR
        6: { cellWidth: 18 },  // SCREEN SIZE
        7: { cellWidth: 28 },  // WIN LABEL
        8: { cellWidth: 36, fontStyle: 'bold', textColor: TEAL_TEXT },  // SCREEN LABEL — bold teal
        9: { cellWidth: 'auto' },  // WIN TYPE — fills remaining space
      },
      margin:         { left: MARGIN, right: MARGIN, bottom: FOOTER_H + 6 },
      tableLineColor: LIGHT_GRAY,
      tableLineWidth: 0.2,

      // Color-code adaptor color and screen size per cell
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        const col = data.column.index;
        const val = String(data.cell.raw ?? '');

        if (col === 5) { // ADAPTOR COLOR
          if (val === 'WHT') {
            data.cell.styles.textColor = WHT_CLR;
            data.cell.styles.fontStyle = 'bold';
          } else if (val === 'TAN') {
            data.cell.styles.textColor = TAN_CLR;
            data.cell.styles.fontStyle = 'bold';
          } else {
            data.cell.styles.textColor = MID_GRAY;
          }
        }

        if (col === 6) { // SCREEN SIZE
          const isHalf = val.toLowerCase().includes('half');
          data.cell.styles.textColor = isHalf ? HALF_CLR : TEAL_TEXT;
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    // Summary box on last page
    if (pageIdx === totalPages - 1) {
      const finalY = doc.lastAutoTable.finalY + 5;
      doc.setFillColor(...TEAL_BG);
      doc.roundedRect(MARGIN, finalY, 70, 8, 2, 2, 'F');
      doc.setTextColor(...TEAL_TEXT);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Screens: ${totalQty}`, MARGIN + 4, finalY + 5.5);
    }
  });

  const customer = (job.customerName || 'Customer').replace(/\s+/g, '_');
  const wo       = job.woNumber || 'DRAFT';
  const filename = `${customer}_WO${wo}_FlexScreenOrder.pdf`;


  // ── Fire-and-forget upload to Firebase Storage ────────────────────────────
  const _blob = doc.output('blob');
  const _contractNum = (job.woNumber || 'DRAFT').replace(/\s+/g, '_');
  uploadJobPDF(_contractNum, 'flexscreen', _blob)
    .then(url => savePDFUrlToJob(job.twpJobId || null, job.id || null, 'flexscreen', url))
    .catch(e => console.warn('PDF upload skipped:', e));
  if (returnBlob) return doc.output('blob');
  doc.save(filename);
}